import { User } from '@/types/user'
import { Box, Button, Flex, Text } from '@chakra-ui/react'
import { Dispatch, SetStateAction, useState } from 'react'
import { ProjectComment as ProjectCommentType } from '../types/projectComment'
import { ProjectCommentForm } from './ProjectCommentForm'
import { UserAvatar } from './UserAvatar'

type Props = {
  comment: ProjectCommentType
  allComments?: ProjectCommentType[]
  reply_to_comment_uuid?: string
  project_uuid: string
  parent_uuid?: string
  setComments: Dispatch<SetStateAction<ProjectCommentType[]>>
  showReplyButton?: boolean
  user?: User
}

// Object that maps numbers to the months of the year
const months = new Map<number, string>([
  [1, 'January'],
  [2, 'February'],
  [3, 'March'],
  [4, 'April'],
  [5, 'May'],
  [6, 'June'],
  [7, 'July'],
  [8, 'August'],
  [9, 'September'],
  [10, 'October'],
  [11, 'November'],
  [12, 'December'],
])

const formatCommentDate = (dateProp?: string): string => {
  if (!dateProp) return ''
  const date = new Date(dateProp)
  return `${months.get(
    date.getMonth() + 1
  )} ${date.getDate()}, ${date.getFullYear()}`
}

const CommentBlock = (props: Props) => {
  const { comment } = props
  const [showForm, setShowForm] = useState<boolean>(false)

  return (
    <Box
      key={comment.uuid}
      my={12}
      borderBottom='1px'
      borderColor='gray.200'
      pb={6}>
      <Box mb={3}>{comment.comment}</Box>
      <Flex alignItems='center'>
        <Flex alignItems='center'>
          {comment.user && <UserAvatar user={comment.user} size='sm' />}
          <Box ml={2}>
            {comment.user && (
              <Text fontSize='sm' fontWeight='bold'>
                {comment.user.first_name}
              </Text>
            )}
            <Text fontSize='xs' color='gray.500'>
              {formatCommentDate(comment.created_at)}
            </Text>
          </Box>
        </Flex>
        {props.showReplyButton && (
          <Button
            ml='auto'
            size='xs'
            onClick={() => {
              setShowForm(!showForm)
            }}>
            {!props.parent_uuid ? 'Reply' : 'Reply to thread'}
          </Button>
        )}
      </Flex>
      <Box pt={4} display={showForm ? 'block' : 'none'}>
        <ProjectCommentForm
          allComments={props.allComments}
          parent_uuid={props.reply_to_comment_uuid}
          project_uuid={props.project_uuid}
          setComments={props.setComments}
          setShowForm={setShowForm}
          user={props.user}
        />
      </Box>
    </Box>
  )
}

export const ProjectComment = (props: Props) => {
  const { comment, allComments } = props
  const childComments: ProjectCommentType[] = allComments
    ? allComments.filter(curComment => comment.uuid === curComment.parent_uuid)
    : []

  return (
    <>
      <CommentBlock
        allComments={props.allComments}
        comment={comment}
        project_uuid={props.project_uuid}
        reply_to_comment_uuid={comment.uuid}
        setComments={props.setComments}
        showReplyButton={childComments.length === 0}
        user={props.user}
      />
      {childComments.map((childComment, i) => (
        <Box ml={16} key={i}>
          <CommentBlock
            allComments={props.allComments}
            comment={childComment}
            project_uuid={props.project_uuid}
            parent_uuid={comment.uuid}
            reply_to_comment_uuid={comment.uuid}
            setComments={props.setComments}
            showReplyButton={childComments.length - 1 === i}
            user={props.user}
          />
        </Box>
      ))}
    </>
  )
}
